// @ts-check

/** @file Privacy-safe sync transport errors and retry classification. */

import DatabasePoolCheckoutTimeoutError from "velocious/build/src/database/pool/checkout-timeout-error.js"

/** Sync transport failure carrying only a stable code and retry decision. */
export class ProjectSyncTransportError extends Error {
  /**
   * Creates a failure containing no server payload or raw exception text.
   * @param {{code: string, retryable: boolean, status?: number | null}} args Safe failure metadata.
   */
  constructor({code, retryable, status = null}) {
    super(`Project sync transport failed: ${code}`)
    this.name = "ProjectSyncTransportError"
    this.code = code
    this.retryable = retryable
    this.status = status
  }
}

/**
 * Maps transport/application failures to coordinator-safe metadata without raw
 * response bodies, payloads, credentials, or exception messages.
 * @param {Error} error Failure to classify.
 * @returns {{code: string, retryable: boolean}} Safe classification.
 */
export function classifyProjectSyncError(error) {
  if (error instanceof ProjectSyncTransportError) {
    return {code: error.code, retryable: error.retryable}
  }

  // A pool checkout timeout is transient contention on the project database, so
  // it must stay retryable instead of collapsing into the permanent sync-failed
  // state that leaves the coordinator without a next retry.
  if (error instanceof DatabasePoolCheckoutTimeoutError) {
    return {code: "database-pool-timeout", retryable: true}
  }

  return {code: "sync-failed", retryable: false}
}
